/**
 * The device inspection as one page of collapsible sections — the same
 * `CollapsibleSection` cards, To do / Done marks and sticky action bar as the
 * session page. Replaced the four-step wizard and its separate read-only
 * summary (2026-10-10).
 *
 * Order is Photos → Identify → Damage → Item status → Other notes. Photos go
 * first because they are taken as the device comes out of the bag, before it
 * is handled, and because they are what a later AI pass will read.
 *
 * - **Saves itself** (`useInspectionAutosave`). There is no Save button.
 * - **Completing needs everything**: identity, every row answered, the serial
 *   label photo or "No visible serial" (`inspectionProgress`, mirrored by sm4rt-api's
 *   `check_in_inspection_rules`). The button stays disabled and says what is
 *   left.
 * - **A completed inspection stays editable and stays complete.** That is what
 *   retired the separate correct-the-serial dialog: Identify is simply editable.
 * - Sections open while outstanding and start collapsed once done, but never
 *   move under the operator (`followStatus={false}`).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Alert, TextField } from '@mui/material';
import { IconCheck, IconLoader2 } from '@tabler/icons-react';
import type { CheckInDraftJob } from '@techgitter/contracts';

import { useInspectionAutosave } from '../../../hooks/useInspectionAutosave';
import { useInspectionDraft } from '../../../hooks/useInspectionDraft';
import { patchCheckInDraftJob } from '../../../services/checkInApi';
import {
  INSPECTION_SCHEMA,
  aiFillPatch,
  bestPhotoReading,
  inspectionProgress,
  type AiFillableField,
} from '../../../utils/inspectionSchema';
import { CollapsibleSection } from '../sessionDetail/CollapsibleSection';
import { DamageSection } from './sections/DamageSection';
import { IdentifySection } from './sections/IdentifySection';
import { ItemStatusSection } from './sections/ItemStatusSection';
import { PhotosSection } from './sections/PhotosSection';

export interface InspectionPanelProps {
  sessionId: string;
  draftJob: CheckInDraftJob;
  /** Cancelled / expired check-in: shown, never changed. */
  readOnly?: boolean;
  /** Refetch the session (photos changed, inspection started). */
  onRefresh: () => void;
  /** After "Mark inspection complete" lands. */
  onCompleted: () => void;
}

export const InspectionPanel: React.FC<InspectionPanelProps> = ({
  sessionId,
  draftJob,
  readOnly = false,
  onRefresh,
  onCompleted,
}) => {
  const deviceType = draftJob.deviceType;
  const schema = INSPECTION_SCHEMA[deviceType];
  const photos = draftJob.photos ?? [];
  const isComplete = draftJob.inspectionStatus === 'complete';

  const { draft, setField, setDamageCheck, setItemStatusCheck, markAllChecksNo } =
    useInspectionDraft(deviceType, draftJob.inspection);
  const autosave = useInspectionAutosave({
    sessionId,
    draftJobId: draftJob.id,
    deviceType,
    draft,
    inspectionStatus: draftJob.inspectionStatus,
    enabled: !readOnly,
    onStarted: onRefresh,
  });
  const hasSerialPhoto = photos.some(p => p.shot === 'serial_label');
  const progress = inspectionProgress(schema, draft, hasSerialPhoto);

  // The AI rig's label reading (psar01) fills Identify's BLANK fields, tagged
  // "read from photo" until someone edits them. Done here rather than on the
  // server because this page autosaves its own copy of the inspection — a
  // server-side write would be overwritten by the blank it still holds. Each
  // reading is applied once per visit, so a field staff deliberately cleared
  // is not refilled under them.
  const appliedReadings = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (readOnly) return;
    const reading = bestPhotoReading(photos);
    if (!reading || appliedReadings.current.has(reading.photoId)) return;
    appliedReadings.current.add(reading.photoId);
    const patch = aiFillPatch(schema, draft, reading);
    if (patch.manufacturer !== undefined) setField('manufacturer', patch.manufacturer);
    if (patch.serialNumber !== undefined) setField('serialNumber', patch.serialNumber);
    if (patch.modelNumber !== undefined) setField('modelNumber', patch.modelNumber);
    if (patch.emcNumber !== undefined) setField('emcNumber', patch.emcNumber);
    if (patch.aiFilled) setField('aiFilled', patch.aiFilled);
    // Runs when a reading arrives (photos refetched), not on every keystroke:
    // `draft` is read for which fields are blank at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, readOnly]);

  /** A staff edit clears the "read from photo" tag on that field. */
  const editIdentity = (key: AiFillableField, value: string | null) => {
    setField(key, value);
    if (draft.aiFilled[key]) setField('aiFilled', { ...draft.aiFilled, [key]: null });
  };
  const readingPending = photos.some(p => p.aiReading?.status === 'pending');

  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const handleComplete = async () => {
    setCompleting(true);
    setCompleteError(null);
    try {
      // The server judges the stored answers, so they must have landed first.
      await autosave.flush();
      await patchCheckInDraftJob(sessionId, draftJob.id, { inspectionStatus: 'complete' });
      onCompleted();
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : 'Failed to complete the inspection');
    } finally {
      setCompleting(false);
    }
  };

  // Status marks only while the inspection is open; on a read-only record they
  // would claim work is owed on a check-in nobody can change.
  const mark = <T,>(status: T) => (readOnly ? undefined : status);

  return (
    <>
      <CollapsibleSection title={`Photos (${photos.length})`} status={mark(progress.photos)} followStatus={false}>
        <PhotosSection
          sessionId={sessionId}
          draftJobId={draftJob.id}
          photos={photos}
          schema={schema}
          brandNewInBox={draft.brandNewInBox}
          noVisibleSerial={draft.serialNotLegible}
          onNoVisibleSerialChange={value => setField('serialNotLegible', value)}
          readingPending={readingPending}
          readOnly={readOnly}
          onPhotosChanged={onRefresh}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Identify" status={mark(progress.identify)} followStatus={false}>
        <IdentifySection
          schema={schema}
          draft={draft}
          readOnly={readOnly}
          onChange={patch => {
            if ('manufacturer' in patch) editIdentity('manufacturer', patch.manufacturer ?? null);
            if ('serialNumber' in patch) editIdentity('serialNumber', patch.serialNumber ?? null);
            if ('modelNumber' in patch) editIdentity('modelNumber', patch.modelNumber ?? null);
            if ('emcNumber' in patch) editIdentity('emcNumber', patch.emcNumber ?? null);
            if ('brandNewInBox' in patch) setField('brandNewInBox', !!patch.brandNewInBox);
          }}
        />
      </CollapsibleSection>

      {progress.damage && (
        <CollapsibleSection title="Damage" status={mark(progress.damage)} followStatus={false}>
          <DamageSection
            rows={schema.damageRows}
            checks={draft.damageChecks}
            readOnly={readOnly}
            onChangeCheck={setDamageCheck}
            onMarkAllNo={() => markAllChecksNo('damage', schema.damageRows.map(r => r.key))}
          />
        </CollapsibleSection>
      )}

      {progress.itemStatus && (
        <CollapsibleSection title="Item status" status={mark(progress.itemStatus)} followStatus={false}>
          <ItemStatusSection
            rows={schema.itemStatusRows}
            checks={draft.itemStatusChecks}
            readOnly={readOnly}
            onChangeCheck={setItemStatusCheck}
            onMarkAllNo={() => markAllChecksNo('itemStatus', schema.itemStatusRows.map(r => r.key))}
          />
        </CollapsibleSection>
      )}

      {/* Optional, so it carries no status mark. */}
      <CollapsibleSection title="Other notes" defaultOpen={!readOnly || !!draft.otherNotes}>
        {readOnly ? (
          <div className={`ci-awaiting ci-pre-wrap${draft.otherNotes ? '' : ' ci-def__value--empty'}`}>
            {draft.otherNotes || '—'}
          </div>
        ) : (
          <TextField
            placeholder="Anything else worth recording — staff-only."
            value={draft.otherNotes ?? ''}
            onChange={e => setField('otherNotes', e.target.value || null)}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
          />
        )}
      </CollapsibleSection>

      {completeError && <Alert severity="error">{completeError}</Alert>}

      {!readOnly && (
        <div className="checkin-sticky-bottom-bar">
          <AutosaveLine
            state={autosave.state}
            error={autosave.error}
            completeNote={isComplete ? completedNote(draftJob) : null}
            pending={isComplete ? [] : progress.summary}
          />
          {!isComplete && (
            <button
              type="button"
              className="ci-btn ci-btn--success ci-btn--touch"
              onClick={handleComplete}
              disabled={completing || progress.gaps.length > 0}
              title={progress.gaps.length ? `Still needed: ${progress.gaps.join('; ')}` : undefined}
            >
              {completing ? (
                <IconLoader2 size={16} stroke={1.5} className="ci-spin" />
              ) : (
                <IconCheck size={16} stroke={2} />
              )}
              Mark inspection complete
            </button>
          )}
        </div>
      )}
    </>
  );
};

const AutosaveLine: React.FC<{
  state: ReturnType<typeof useInspectionAutosave>['state'];
  error: string | null;
  completeNote: string | null;
  pending: string[];
}> = ({ state, error, completeNote, pending }) => {
  if (state === 'error') {
    return (
      <div className="ci-autosave ci-autosave--error" title={error ?? undefined}>
        Couldn&apos;t save — retrying…
      </div>
    );
  }
  const saving = state === 'saving' ? 'Saving…' : state === 'saved' ? 'All changes saved' : null;
  const lead = completeNote ?? (pending.length ? `Still needed: ${pending.join(' · ')}` : 'Ready to complete');
  return (
    <div className="ci-autosave">
      {lead}
      {saving && <> · {saving}</>}
    </div>
  );
};

/** "Complete · 10 Oct, 3:04 pm by steve@…" — business timestamps are Sydney time. */
function completedNote(draftJob: CheckInDraftJob): string {
  const at = draftJob.inspectionCompletedAt ? new Date(draftJob.inspectionCompletedAt) : null;
  const when =
    at && !Number.isNaN(at.getTime())
      ? at.toLocaleString('en-AU', {
          timeZone: 'Australia/Sydney',
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      : null;
  return [
    'Inspection complete',
    when,
    draftJob.inspectionCompletedBy ? `by ${draftJob.inspectionCompletedBy}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
